<template>
    <label class="f fv pr ath-form-search" :for="uid">
        <div class="f fh fa-c dw-10 sm-b-xs" v-if="lbl">
            <slot name="labelLeft"></slot>
            <span class="fg ath-form-search-label">{{ uiTitle }}<slot name="labelAfter"></slot></span>
            <slot name="labelRight"></slot>
        </div>
        <!-- Input -->
        <div class="dw-10 pr">
            <input ref="input"
                novalidate
                class="dw-10"
                type="search"
                @input="update"
                :value="value"
                :name="uid"
                :id="uid"
                :required="required"
                :disabled="disabled"
                :placeholder="$options.filters.athT(placeholder)" />
            <VisualIcon name="magnifier" class="pa-lc sp-l-s sp-r-s u-pe-n" />
            <div class="f fv fj-c pa-rc sp-r-xs">
                <ButtonIcon v-if="hasValue && !disabled"
                    icon="cross"
                    class="el--s-xs el--t-default"
                    @click.prevent.stop="clear" />
            </div>
        </div>
    </label>
</template>

<script>
import { guuid, isNeString, isNumber, join } from '@aretheyhappy/utils';
import VisualIcon from '../visuals/Icon.vue';
import ButtonIcon from '../ButtonIcon.vue';

export default {
    name: 'mead-form-search',
    components: {VisualIcon, ButtonIcon},
    props: {
        /**
         * Label i18n key rendered above the search input. Only shown when truthy.
         */
        lbl         : {type: [String, Boolean], default: () => false},
        /**
         * Entity name interpolated into the label when translating it.
         */
        lblEntity   : {type: [Boolean, String], default: () => false},
        /**
         * Whether the interpolated entity name should be lowercased in the translated label.
         */
        lblEntityLc : {type: Boolean, default: () => true},
        /**
         * Placeholder text (i18n key) shown in the input when empty.
         */
        placeholder : {type: [String, Boolean], default: () => false},
        /**
         * Current search text (v-model).
         */
        value       : {type: String, default: () => ''},
        /**
         * Disables the input and hides the clear button.
         */
        disabled    : {type: Boolean, default: () => false},
        /**
         * Milliseconds to debounce the `input` emission by; `false` emits on every keystroke.
         */
        debounce    : {type: [Number, Boolean], default: () => 750},
        /**
         * Marks the input as required.
         */
        required    : {type: Boolean, default: () => false},
        /**
         * Function invoked when the input receives focus, attached as a native `focus` listener.
         */
        uiOnFocus   : {type: [Boolean, Function], default: () => false},
        /**
         * Automatically focuses the input once mounted.
         */
        uiAutoFocus : {type: Boolean, default: () => false},
        /**
         * Appends an "(optional)" suffix to the label.
         */
        uiOptional  : {type: Boolean, default: () => false},
    },
    data () {
        return {
            input               : isNeString(this.value) ? this.value : '',
            uid                 : guuid(),
            ui_debounce         : isNumber(this.debounce) ? parseInt(this.debounce) : false,
            ui_debounce_timer   : false,
        };
    },
    methods: {
        clear: function () {
            this.$refs.input.value = '';
            this.$emit('input', this.$refs.input.value);
            this.$emit('actClear');
        },
        update: function () {
            if (this.ui_debounce === false) {
                this.$emit('input', this.$refs.input.value);
            } else {
                if (this.ui_debounce_timer) clearTimeout(this.ui_debounce_timer);
                this.ui_debounce_timer = setTimeout(
                    () => this.$emit('input', this.$refs.input.value),
                    this.ui_debounce
                );
            }
        },
    },
    computed: {
        uiTitle: function () {
            return join([
                this.lblEntity
                    ? MeadCommon.I18N[this.lblEntityLc ? 'actionLc' : 'action'](this.lbl, this.lblEntity)
                    : MeadCommon.I18N.go(this.lbl),
                this.uiOptional
                    ? `(${MeadCommon.I18N.go('mead.labels.optional')})`
                    : false,
            ], {delim: ' '});
        },
        hasValue: function () {
            return isNeString(this.value);
        },
    },
    async mounted () {
        //  UIAutoFocus
        if (this.$refs.input && this.uiAutoFocus) {
            await this.$nextTick();
            this.$refs.input.focus();
        }

        //  Add uiOnFocus
        if (this.$refs.input && this.uiOnFocus) {
            this.$refs.input.addEventListener('focus', this.uiOnFocus);
        }
    },
};
</script>

<style lang="scss" scoped>
@use '../../../../sass/variables' as *;

input {
    appearance: none;
    font-family: $ff-b;
    font-weight: 400;
    font-size: $fs-s;
    height: 3rem;
    padding: 0 calc($s-xl + $s-xs);
    border: none;
    border-radius: $br-m;
    line-height: initial;
    box-shadow: inset 0 0 4rem 4rem var(--c-brand-light-bg);
    background-color: var(--c-brand-light-bg);
    color: var(--c-brand-light-fg);

    &[disabled="disabled"] {
        cursor: not-allowed;
        opacity: .65;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-internal-autofill-selected {
        color: var(--c-brand-light-fg) !important;
        -webkit-text-fill-color: var(--c-brand-light-fg) !important;
    }

    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-decoration,
    &::-webkit-search-results-button {
        display: none;
    }

    &:active,
    &:focus {
        border-color: transparent;
    }
}

.ath-form-search-label {
    font-family: $ff-b;
    font-weight: 400;
    line-height: $lh-xs;
    font-size: $fs-s;
    color: var(--c-txt);
}

.ath-form-search {
    &.el--invalid input {
        outline: 1px solid var(--c-brand-danger-bg);
    }
}

//  SIZES

@mixin inputSize ($name, $height) {
    .ath-form-search.el--s-#{$name} input {
        height: $height;
    }
}

@include inputSize('s', 3rem);
@include inputSize('m', 4rem);
@include inputSize('l', 5rem);
</style>
